import { ContentGrid, ContentGridItem, useContentArea, withinOrWiderThan } from '@udemy/react-layout-components';
import classNames from 'classnames';
import React from 'react';
import { CardSecondaryContent } from './card-secondary-content.react-component';
import commonStyles from "./common-card.module.css";
import { CondensedCard } from './condensed-card.react-component';
import styles from "./horizontal-card.module.css";
/** React props for `HorizontalCard` component */

/**
 * ### HorizontalCard component
 *
 * @remarks
 *
 * #### Layout Switching
 *
 * A card layout intended for consuming the full width of a content area. When rendered
 * in wider content areas, the card will use an expanded layout. The media content will
 * align with the content area grid.
 *
 * At or below `xs` content area ranges, this card will render with the condensed card
 * layout.
 *
 * Render this card in a layout that includes the `ContentArea` component, and within
 * a `ContentGrid` item.
 *
 * @see `@udemy/react-layout-components#ContentArea`
 * @see `@udemy/react-layout-components#ContentGrid`
 *
 * #### Media
 *
 * Pass both `mediaSquare` and `mediaWide` props to provide media content for the card.
 * The card will automatically apply the media content appropriate for the current layout.
 *
 * #### Secondary Content
 *
 * While the card supports two secondary content areas, it will only render one at a
 * time. In the expanded layout, `inlineSecondaryContent` will override
 * `secondaryContent` if both are provided. In the condensed layout, only
 * `secondaryContent` will be rendered
 */
export const HorizontalCard = _ref => {
  let {
    className,
    ...props
  } = _ref;
  const {
    range,
    isRangeKnown
  } = useContentArea(); // If range is not known (server-side, or first render), then render both
  // layouts and rely on CSS to hide the appropriate layout

  const showExpandedLayout = !isRangeKnown || withinOrWiderThan(range, 'sm');
  const showCondensedLayout = !isRangeKnown || !withinOrWiderThan(range, 'sm');
  const {
    mediaWide,
    mediaSquare,
    ...expandedCardProps
  } = props;
  const {
    inlineSecondaryContent,
    ...condensedCardProps
  } = expandedCardProps;
  return /*#__PURE__*/React.createElement(React.Fragment, null, showExpandedLayout && /*#__PURE__*/React.createElement(ExpandedCard, Object.assign({
    className: classNames(styles['expanded-layout'], className),
    media: mediaWide
  }, expandedCardProps)), showCondensedLayout && /*#__PURE__*/React.createElement(CondensedCard, Object.assign({
    className: classNames(styles['condensed-layout'], className),
    media: mediaSquare
  }, condensedCardProps)));
};
/**
 * Props for the `ExpandedCard` component
 *
 * @internal
 */

/**
 * ### ExpandedCard component
 *
 * @remarks
 *
 * Card layout for wider content areas, rendered by the HorizontalCard component.
 *
 * It assumes that is rendered within a `ContentGrid` component.
 *
 * @internal
 **/
const ExpandedCard = _ref2 => {
  var _ref3, _ref4;

  let {
    action,
    authors,
    className,
    description,
    iconButton,
    inlineSecondaryContent,
    media,
    progress,
    secondaryContent,
    showSecondaryContentBorder = true,
    tags,
    title,
    statusLabel,
    xCenterPrimaryContentVertically = false
  } = _ref2;
  const {
    range,
    isRangeKnown
  } = useContentArea(); // If range is not known (server-side, or first render), then render all
  // secondary content and rely on CSS to determine its visibility based on
  // the content area range with container queries.

  const renderInlineSecondaryContent = inlineSecondaryContent && (!isRangeKnown || withinOrWiderThan(range, 'lg'));
  const renderSecondaryContent = secondaryContent && (!isRangeKnown || !(inlineSecondaryContent && withinOrWiderThan(range, 'lg'))); // Media column spans align with 4-course card carousel layout

  const mediaColumnDefaultSpan = 2;
  const mediaColumnMdSpan = 4;
  const mediaColumnLgSpan = 3;
  const hasPrimaryBottom = (_ref3 = (_ref4 = tags !== null && tags !== void 0 ? tags : progress) !== null && _ref4 !== void 0 ? _ref4 : statusLabel) !== null && _ref3 !== void 0 ? _ref3 : action;
  return /*#__PURE__*/React.createElement("section", {
    className: classNames(styles.card, className)
  }, /*#__PURE__*/React.createElement(ContentGrid, {
    className: styles['horizontal-content']
  }, /*#__PURE__*/React.createElement(ContentGridItem, {
    className: styles['media-column'],
    defaultSpan: mediaColumnDefaultSpan,
    mdSpan: mediaColumnMdSpan,
    lgSpan: mediaColumnLgSpan
  }, media), /*#__PURE__*/React.createElement(ContentGridItem, {
    className: styles['content-column']
    /* Fill the remaining columns available to the card */
    ,
    defaultColumn: `${mediaColumnDefaultSpan + 1} / -1`,
    mdColumn: `${mediaColumnMdSpan + 1} / -1`,
    lgColumn: `${mediaColumnLgSpan + 1} / -1`
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.primary
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['primary-content'], {
      [styles['primary-content-centered']]: xCenterPrimaryContentVertically
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(commonStyles['primary-head'], styles['primary-head'])
  }, /*#__PURE__*/React.createElement("div", null, title), description && /*#__PURE__*/React.createElement("div", null, description), authors && /*#__PURE__*/React.createElement("div", null, authors)), hasPrimaryBottom && /*#__PURE__*/React.createElement("div", {
    className: styles['primary-bottom']
  }, tags && /*#__PURE__*/React.createElement("div", null, tags), progress && /*#__PURE__*/React.createElement("div", {
    className: styles.progress
  }, progress), (statusLabel || action) && /*#__PURE__*/React.createElement("div", {
    className: commonStyles['primary-content-spread']
  }, statusLabel && /*#__PURE__*/React.createElement("div", null, statusLabel), action && /*#__PURE__*/React.createElement("div", null, action)))), iconButton && /*#__PURE__*/React.createElement("div", {
    className: styles['icon-button']
  }, iconButton)), renderInlineSecondaryContent && /*#__PURE__*/React.createElement("div", {
    className: styles['inline-secondary']
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['inline-secondary-content'], {
      [styles['without-border']]: !showSecondaryContentBorder
    })
  }, inlineSecondaryContent)))), renderSecondaryContent && /*#__PURE__*/React.createElement(CardSecondaryContent, {
    className: classNames(styles['secondary-content']),
    showBorder: showSecondaryContentBorder
  }, secondaryContent));
};
//# sourceMappingURL=horizontal-card.react-component.js.map